import {Injectable} from '@angular/core';
import {ApiService} from './api.service';
import {AuthService} from './auth.service';
import {User} from '@app/models';
import {Router} from "@angular/router";

@Injectable({
  providedIn: 'root'
})
export class SettingsService {
  private settingsData: any;
  private retryCounter: number = 0;
  private settingsPromise: Promise<any> | null = null;

  constructor(private apiService: ApiService, public authService: AuthService, private router: Router) {
  }

  async getSettings(): Promise<any> {
    if (this.retryCounter >= 3) {
      return null;
    }

    if (this.settingsData) {
      return this.settingsData;
    }

    if (!this.authService.userValue) {
      return null;
    }

    if (this.settingsPromise) {
      return this.settingsPromise;
    }

    this.settingsPromise = this.apiService.getFromURL('/api/v1/settings.json')
      .then(data => {
        this.settingsData = data;
        this.settingsPromise = null;
        return data;
      })
      .catch(error => {
        this.retryCounter += 1;
        this.settingsPromise = null;
        console.error('Error fetching settings:', error);
        throw error;
      });

    return this.settingsPromise;
  }

  clearSettingsData(): void {
    this.settingsData = null;
    this.settingsPromise = null;
    this.retryCounter = 0;
  }

  async getAvailableModules(): Promise<string[]> {
    const settings = await this.getSettings();
    return settings ? settings.available_modules : [];
  }

  // App sections like organization, clients and template_management. They used
  // to come mixed into available_modules.
  async getAvailableSections(): Promise<string[]> {
    const settings = await this.getSettings();
    return settings?.available_sections || [];
  }

  async getCurrentUser(): Promise<User> {
    const settings = await this.getSettings();
    return settings ? settings.current_user : undefined;
  }

  async getClientFeatures(): Promise<User> {
    const settings = await this.getSettings();
    return settings ? settings.client_features : undefined;
  }

  async getHasAccess(): Promise<User> {
    const settings = await this.getSettings();
    return settings ? settings.has_access : undefined;
  }

  async getUserRoles(): Promise<string[]> {
    const settings = await this.getSettings();
    return settings ? settings.user_roles : [];
  }

  async getUserPermissions(): Promise<string[]> {
    const settings = await this.getSettings();
    return settings ? settings.permissions : [];
  }

  async getDisplaySso(): Promise<boolean> {
    const settings = await this.getSettings();
    return settings ? settings?.client_features?.display_sso : false;
  }

  async getAlternativeEmailEnabled(): Promise<boolean> {
    const settings = await this.getSettings();
    return settings ? settings?.client_features?.alternative_email_enabled : false;
  }

  async getHierarchyEnabled(): Promise<boolean> {
    const settings = await this.getSettings();
    return settings ? settings?.client_features?.hierarchy_enabled : false;
  }

  async getNohl5by5(): Promise<boolean> {
    const settings = await this.getSettings();
    return settings ? settings?.client_features?.uses_nohl_matrix_5_by_5 : false;
  }

  async getEyeAbleAccess(): Promise<boolean> {
    const settings = await this.getSettings();
    return settings ? settings?.client_features?.eye_able_access : false;
  }

  async getInterfaceToEasybill(): Promise<boolean> {
    const settings = await this.getSettings();
    return settings ? settings?.client_features?.interface_to_easybill : false;
  }

  async getPersonioActive(): Promise<boolean> {
    const settings = await this.getSettings();
    return settings ? settings?.client_features?.personio_active : false;
  }

  async getCabinetManagement(): Promise<boolean> {
    const settings = await this.getSettings();
    return settings ? settings?.client_features?.cabinet_management : false;
  }

  async getAiEnabled(): Promise<boolean> {
    const settings = await this.getSettings();
    return !!settings?.client_features?.ai_enabled;
  }
}
